import React, { useState, useEffect, useRef } from "react";
import { Mic, Cloud, ArrowUp, Upload } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface MainInterfaceProps {
  onStartRecording: () => void;
  onGoogleDriveUpload: () => void;
  onFileDrop: (acceptedFiles: File[]) => void;
  isReadOnly?: boolean;
  isArchiveMode?: boolean;
  /** Disable start/upload while another upload or transcription is in flight. */
  isBusy?: boolean;
}

const MainInterface: React.FC<MainInterfaceProps> = ({
  onStartRecording,
  onGoogleDriveUpload,
  onFileDrop,
  isReadOnly = false,
  isArchiveMode = false,
  isBusy = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const controlsDisabled = isReadOnly || isArchiveMode || isBusy;

  // Handle file input change
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isBusy) {
      toast.error("העלאה בתהליך — נא להמתין לסיום");
      event.target.value = "";
      return;
    }
    if (isReadOnly || isArchiveMode) {
      toast.error("לא ניתן להעלות קבצים - תקופת הניסיון הסתיימה. שדרג למינוי בתשלום.");
      return;
    }

    const files = event.target.files;
    if (files && files.length > 0) {
      if (files[0].size <= 0) {
        toast.error("קובץ השמע ריק. בדוק את המיקרופון ונסה להקליט או להעלות שוב.");
        event.target.value = "";
        return;
      }
      const supportedFiles = Array.from(files).filter((file) =>
        file.type.startsWith("audio/") || file.type === "video/mp4" || file.type === "video/webm"
      );

      if (supportedFiles.length > 0) {
        toast.success("קובץ נבחר בהצלחה!");
        onFileDrop(supportedFiles);
      } else {
        toast.error("קובץ לא נתמך. אנא בחר קובץ אודיו או MP4 בלבד");
      }
    }
  };

  // Handle button click to open file picker
  const handleFileButtonClick = () => {
    if (isBusy) {
      toast.error("העלאה בתהליך — נא להמתין לסיום");
      return;
    }
    if (isReadOnly || isArchiveMode) {
      toast.error("לא ניתן להעלות קבצים - תקופת הניסיון הסתיימה. שדרג למינוי בתשלום.");
      return;
    }
    fileInputRef.current?.click();
  };

  // Global drag detection
  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) {
        setIsDragOver(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current -= 1;
      if (dragCounterRef.current <= 0) {
        setIsDragOver(false);
        dragCounterRef.current = 0;
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      dragCounterRef.current = 0;

      if (isBusy || isReadOnly || isArchiveMode) {
        return;
      }

      // Handle file drop
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        // Filter supported files (audio and MP4)
        console.log("files", files);
        console.log("files", files[0].type);
        const supportedFiles = Array.from(files).filter(
          (file) => file.type.startsWith("audio/") || file.type === "video/mp4" || file.type === "video/webm"
        );

        if (supportedFiles.length > 0) {
          // Show success toast
          toast.success("קובץ נבחר בהצלחה!");
          // Call the file drop handler
          onFileDrop(supportedFiles);
        } else {
          // Show error toast for unsupported files
          toast.error("קובץ לא נתמך. אנא בחר קובץ אודיו או MP4 בלבד");
        }
      }
    };

    document.addEventListener("dragenter", handleDragEnter);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("drop", handleDrop);

    return () => {
      document.removeEventListener("dragenter", handleDragEnter);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("drop", handleDrop);
    };
  }, [onFileDrop, isBusy, isReadOnly, isArchiveMode]);
  // Wrapper handlers for read-only mode
  const handleStartRecording = () => {
    if (isBusy) {
      toast.error("העלאה בתהליך — נא להמתין לסיום");
      return;
    }
    if (isReadOnly || isArchiveMode) {
      toast.error("לא ניתן להקליט - תקופת הניסיון הסתיימה. שדרג למינוי בתשלום.");
      return;
    }
    onStartRecording();
  };

  const handleGoogleDriveUpload = () => {
    if (isBusy) {
      toast.error("העלאה בתהליך — נא להמתין לסיום");
      return;
    }
    if (isReadOnly || isArchiveMode) {
      toast.error("לא ניתן להעלות מגוגל דרייב - תקופת הניסיון הסתיימה. שדרג למינוי בתשלום.");
      return;
    }
    onGoogleDriveUpload();
  };

  const tileClass = cn(
    "relative flex min-h-[96px] w-full flex-col items-center justify-center gap-1.5 rounded-xl px-3 py-3 outline-none transition-colors",
    "focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
    controlsDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
  );

  return (
    <>
      {/* Drag overlay */}
      {isDragOver && (
        <div className="fixed inset-0 z-50 bg-primary/20 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-background border-2 border-dashed border-primary rounded-lg p-8 text-center max-w-md mx-4">
            <Upload className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-chen-900 mb-2">שחרר כאן</h2>
            <p className="text-muted-foreground">שחרר קובץ אודיו או MP4 כאן להעלאה</p>
          </div>
        </div>
      )}

      <div className="container mx-auto flex max-w-3xl flex-col justify-center gap-8 p-6">
        <h1 className="text-center text-lg font-semibold text-chen-900">בחרו באופציה הרצויה</h1>

        <div className="mx-auto grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
          <button
            type="button"
            onClick={handleStartRecording}
            disabled={controlsDisabled}
            aria-label="התחל הקלטה"
            className={tileClass}
          >
            <span className="group relative inline-flex">
              <span
                className="absolute inset-0 rounded-full bg-red-500 opacity-20 transition-transform duration-300 group-hover:scale-110"
                aria-hidden
              />
              <span className="relative z-10 flex size-10 items-center justify-center rounded-full bg-red-500 text-white shadow-md">
                <Mic className="size-4" strokeWidth={2.25} aria-hidden />
              </span>
            </span>
            <span className="text-center text-xs font-medium leading-4 text-chen-900">הקלטה</span>
            <span className="text-center text-[10px] text-muted-foreground">לחץ להתחלה</span>
          </button>

          <button
            type="button"
            onClick={handleFileButtonClick}
            disabled={controlsDisabled}
            aria-label="בחר קובץ"
            className={cn(tileClass, "border border-dashed border-primary/30", !controlsDisabled && "hover:bg-primary/5")}
          >
            <span className="flex size-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
              <ArrowUp className="size-4" strokeWidth={2.5} aria-hidden />
            </span>
            <span className="text-center text-xs font-medium leading-4 text-chen-900">בחר קובץ</span>
            <span className="text-center text-[10px] font-medium text-muted-foreground">MP3 · WAV · M4A · WEBM · MP4</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,video/mp4"
              multiple={false}
              className="hidden"
              onChange={handleFileInputChange}
              disabled={controlsDisabled}
            />
          </button>

          <button
            type="button"
            onClick={handleGoogleDriveUpload}
            disabled={controlsDisabled}
            aria-label="העלאה מגוגל דרייב"
            className={cn(
              tileClass,
              "border border-border bg-muted/50",
              !controlsDisabled && "hover:border-primary/40 hover:bg-primary/5"
            )}
          >
            <span className="flex size-10 items-center justify-center rounded-full bg-background text-primary shadow-sm">
              <Cloud className="size-4" strokeWidth={2.25} aria-hidden />
            </span>
            <span className="text-center text-xs font-medium leading-4 text-chen-900">גוגל דרייב</span>
            <span className="text-center text-[10px] text-muted-foreground">העלאה מהענן</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default MainInterface;
